import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Modal,
  Progress,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import PageHeader from '@shared/PageHeader'
import {
  stockTakingService,
  type ShelfDetail,
  type ShelfItem,
  type StockTaking,
} from '@services/operator/stockTakingService'

import {
  CountedQuantityCell,
  NoteCell,
  type CellSettersRef,
  type ShelfItemDraft,
  type ShelfItemDraftRef,
} from './components/ShelfItemCells'
import { useShelfItemsLoader } from './hooks/useShelfItemsLoader'

const { Text } = Typography

const VIRTUAL_SCROLL_HEIGHT = 600

// 編輯流程裡 row 用 inventory_id 當 React key（同個 shelf 內唯一）。
// confirm payload 也以 inventory_id 為主鍵。
type MergedRow = {
  inventory_id: string
  product_name: string | null
  sku: string | null
  inventory_type_name: string | null
  reference_quantity: number | null
  expected_quantity: number | null
  counted_quantity: number | null
  note: string | null
  counted_at: string | null
  isReference: boolean // 動態模式才會出現 false（帳外品）
}

function mergeStaticItems(items: ShelfItem[]): MergedRow[] {
  return items.map((item) => ({
    inventory_id: item.inventory_id,
    product_name: item.product_name,
    sku: item.sku,
    inventory_type_name: item.inventory_type_name,
    reference_quantity: item.expected_quantity, // 靜態模式：snapshot at create-time
    expected_quantity: item.expected_quantity,
    counted_quantity: item.counted_quantity,
    note: item.note,
    counted_at: item.counted_at,
    isReference: true,
  }))
}

function mergeDynamicItems(shelf: ShelfDetail): MergedRow[] {
  const itemsMap = new Map<string, ShelfDetail['items'][number]>()
  shelf.items.forEach((item) => itemsMap.set(item.inventory_id, item))
  const referenceMap = new Map<string, ShelfDetail['reference_inventories'][number]>()
  shelf.reference_inventories.forEach((inv) => referenceMap.set(inv.inventory_id, inv))

  const merged: MergedRow[] = []

  shelf.reference_inventories.forEach((ref) => {
    const item = itemsMap.get(ref.inventory_id)
    merged.push({
      inventory_id: ref.inventory_id,
      product_name: ref.product_name,
      sku: ref.sku,
      inventory_type_name: ref.inventory_type_name,
      reference_quantity: ref.quantity,
      expected_quantity: item?.expected_quantity ?? null,
      counted_quantity: item?.counted_quantity ?? null,
      note: item?.note ?? null,
      counted_at: item?.counted_at ?? null,
      isReference: true,
    })
  })

  // 帳外品：已盤但不在 reference 中
  shelf.items.forEach((item) => {
    if (!referenceMap.has(item.inventory_id)) {
      merged.push({
        inventory_id: item.inventory_id,
        product_name: item.product_name,
        sku: item.sku,
        inventory_type_name: item.inventory_type_name,
        reference_quantity: 0,
        expected_quantity: item.expected_quantity,
        counted_quantity: item.counted_quantity,
        note: item.note,
        counted_at: item.counted_at,
        isReference: false,
      })
    }
  })

  return merged
}

function seedDraftFromRows(
  draftRef: ShelfItemDraftRef,
  rows: MergedRow[]
) {
  rows.forEach((row) => {
    draftRef.current.set(row.inventory_id, {
      counted_quantity: row.counted_quantity,
      note: row.note ?? '',
    })
  })
}

export default function StockTakingShelfEdit() {
  const { id: stockTakingId, shelfId } = useParams<{ id: string; shelfId: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [metaLoading, setMetaLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [shelfMeta, setShelfMeta] = useState<ShelfDetail | null>(null)
  const [stockTaking, setStockTaking] = useState<StockTaking | null>(null)
  const [metaError, setMetaError] = useState<string | null>(null)

  // 用 ref 保存所有 row 的 draft（counted_quantity + note），避免每次打字觸發父層 setState。
  // submit 時統一從 draftRef.current 讀。
  const draftRef = useRef<Map<string, ShelfItemDraft>>(new Map())
  const cellSettersRef = useRef<Map<string, (v: number | null) => void>>(new Map())

  // 已填寫筆數：節流更新，僅供 Descriptions 區塊顯示
  const [filledCount, setFilledCount] = useState(0)

  const isStatic = stockTaking?.mode === 'static'
  const isCompleted = stockTaking?.status === 'completed'

  // Static mode 才啟用 paged loader；dynamic / 還沒抓到 meta / 已 fail 都不啟動
  const itemsLoader = useShelfItemsLoader({
    stockTakingId: stockTakingId ?? null,
    shelfId: shelfId ?? null,
    enabled: Boolean(stockTakingId && shelfId && isStatic && !metaError),
  })

  // 載入 stock_taking + shelf metadata（小 payload）
  useEffect(() => {
    if (!stockTakingId || !shelfId) return
    let cancelled = false

    const loadMeta = async () => {
      setMetaLoading(true)
      setMetaError(null)
      try {
        const [stRes, shelfRes] = await Promise.all([
          stockTakingService.get(stockTakingId),
          stockTakingService.getShelf(stockTakingId, shelfId),
        ])
        if (cancelled) return

        if (!stRes.success) {
          setMetaError(stRes.message || t('common.error.load_failed'))
          return
        }
        if (!shelfRes.success) {
          setMetaError(shelfRes.message || t('common.error.load_failed'))
          return
        }

        setStockTaking(stRes.data)
        setShelfMeta(shelfRes.data)
      } catch (err) {
        if (cancelled) return
        console.error('Load shelf meta error:', err)
        setMetaError(t('common.error.network_error'))
      } finally {
        if (!cancelled) setMetaLoading(false)
      }
    }

    void loadMeta()
    return () => {
      cancelled = true
    }
  }, [stockTakingId, shelfId, t])

  // 切換 shelf 時清空 draft 與 filled 計數
  useEffect(() => {
    draftRef.current = new Map()
    cellSettersRef.current = new Map()
    setFilledCount(0)
  }, [stockTakingId, shelfId])

  // 動態模式：items 來自 shelfMeta.items + reference_inventories
  // 靜態模式：items 來自 useShelfItemsLoader 分批 load 完成後的陣列
  const mergedItems = useMemo<MergedRow[]>(() => {
    if (!shelfMeta || !stockTaking) return []
    if (stockTaking.mode === 'static') {
      // 載入中先不渲染（避免 Table 顯示一半的資料給使用者）
      if (itemsLoader.loading || itemsLoader.error) return []
      return mergeStaticItems(itemsLoader.items)
    }
    return mergeDynamicItems(shelfMeta)
  }, [shelfMeta, stockTaking, itemsLoader.items, itemsLoader.loading, itemsLoader.error])

  // mergedItems 改變時 seed draft（包含舊已盤資料），同時刷新 filledCount
  useEffect(() => {
    draftRef.current = new Map()
    seedDraftFromRows(draftRef, mergedItems)
    const initFilled = mergedItems.reduce(
      (n, row) => (row.counted_quantity !== null && row.counted_quantity !== undefined ? n + 1 : n),
      0
    )
    setFilledCount(initFilled)
  }, [mergedItems])

  const handleFilledChange = React.useCallback((_id: string, _isFilled: boolean) => {
    // 不重算整個 Map（萬筆會慢）；用 functional 更新 + draftRef.current 抓當下 snapshot
    const next = Array.from(draftRef.current.values()).reduce(
      (n, d) => (d.counted_quantity !== null && d.counted_quantity !== undefined ? n + 1 : n),
      0
    )
    setFilledCount(next)
  }, [])

  const fillReference = React.useCallback((inventoryId: string, qty: number) => {
    const setter = cellSettersRef.current.get(inventoryId)
    if (setter) setter(qty)
  }, [])

  const handleSubmit = () => {
    if (!stockTakingId || !shelfId) return

    Modal.confirm({
      title: t('shared.stock_takings.shelf.submit_confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('shared.stock_takings.shelf.submit_confirm_content'),
      okText: t('common.action.confirm'),
      cancelText: t('common.action.cancel'),
      onOk: async () => {
        setSubmitting(true)
        try {
          const items = Array.from(draftRef.current.entries())
            .filter(([, draft]) => draft.counted_quantity !== null && draft.counted_quantity !== undefined)
            .map(([inventoryId, draft]) => ({
              inventory_id: inventoryId,
              counted_quantity: draft.counted_quantity,
              note: draft.note || null,
            }))

          const response = await stockTakingService.confirmShelf(stockTakingId, shelfId, items)

          if (response.success) {
            message.success(t('shared.stock_takings.shelf.submit_success'))
            navigate(`/stock_takings/${stockTakingId}`)
          } else {
            message.error(response.message || t('shared.stock_takings.shelf.submit_failed'))
          }
        } catch (err) {
          console.error('Submit shelf count error:', err)
          message.error(t('common.error.network_error'))
        } finally {
          setSubmitting(false)
        }
      },
    })
  }

  const isEditable = !isCompleted && !!stockTaking && stockTaking.mode !== 'dynamic'

  const columns = useMemo<ColumnsType<MergedRow>>(() => {
    const isBlind = stockTaking?.is_blind
    const isReadOnly = !isEditable

    const cols: ColumnsType<MergedRow> = [
      {
        title: t('models.product.sku'),
        dataIndex: 'sku',
        key: 'sku',
        width: 120,
        fixed: 'left',
        render: (sku: string | null) => <Text copyable={sku ? { text: sku } : undefined}>{sku ?? '-'}</Text>,
      },
      {
        title: t('models.product.name'),
        dataIndex: 'product_name',
        key: 'product_name',
        width: 200,
        ellipsis: true,
      },
      {
        title: t('models.inventory.inventory_type'),
        dataIndex: 'inventory_type_name',
        key: 'inventory_type_name',
        width: 100,
        render: (name: string | null) => name || '-',
      },
    ]

    if (!isBlind) {
      cols.push({
        title: (
          <Tooltip title={t('shared.stock_takings.shelf.reference_quantity_hint')}>
            <Space>
              {t('shared.stock_takings.shelf.reference_quantity')}
              <InfoCircleOutlined />
            </Space>
          </Tooltip>
        ),
        dataIndex: 'reference_quantity',
        key: 'reference_quantity',
        width: 130,
        align: 'center',
        render: (qty: number | null, record) => (
          <Space>
            <span>{qty ?? '-'}</span>
            {isEditable && qty !== null && qty > 0 && (
              <Button
                type="link"
                size="small"
                onClick={() => fillReference(record.inventory_id, qty)}
              >
                {t('shared.stock_takings.shelf.fill_reference')}
              </Button>
            )}
          </Space>
        ),
      })
    }

    cols.push({
      title: t('shared.stock_takings.shelf.counted_quantity'),
      key: 'counted_quantity',
      width: 150,
      align: 'center',
      render: (_, record) => {
        if (isReadOnly) {
          const counted = record.counted_quantity
          return counted !== null && counted !== undefined
            ? counted
            : <Tag color="red">{t('shared.stock_takings.shelf.not_counted')}</Tag>
        }
        return (
          <CountedQuantityCell
            inventoryId={record.inventory_id}
            initialValue={record.counted_quantity}
            placeholder={t('shared.stock_takings.shelf.input_quantity')}
            draftRef={draftRef}
            settersRef={cellSettersRef as CellSettersRef}
            onFilledChange={handleFilledChange}
          />
        )
      },
    })

    if (isReadOnly) {
      cols.push({
        title: t('shared.stock_takings.shelf.difference'),
        key: 'difference',
        width: 100,
        align: 'center',
        render: (_, record) => {
          const counted = record.counted_quantity
          if (counted === null || counted === undefined) return '-'
          const diff = counted - (record.reference_quantity ?? 0)
          if (diff === 0) return <span style={{ color: '#52c41a' }}>0</span>
          if (diff > 0) return <Tag color="green">+{diff}</Tag>
          return <Tag color="red">{diff}</Tag>
        },
      })
    }

    cols.push({
      title: t('models.stock_taking_item.note'),
      key: 'note',
      width: 200,
      render: (_, record) => {
        if (isReadOnly) return record.note || '-'
        return (
          <NoteCell
            inventoryId={record.inventory_id}
            initialValue={record.note ?? ''}
            placeholder={t('shared.stock_takings.shelf.note_placeholder')}
            draftRef={draftRef}
          />
        )
      },
    })

    return cols
  }, [t, isEditable, stockTaking?.is_blind, fillReference, handleFilledChange])

  // ====== Render guards ======

  if (metaLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (metaError) {
    return (
      <Alert
        message={t('common.error.title')}
        description={metaError}
        type="error"
        showIcon
      />
    )
  }

  if (!shelfMeta || !stockTaking) {
    return (
      <Alert
        message={t('common.error.not_found')}
        type="warning"
        showIcon
      />
    )
  }

  const isLoadingItems = isStatic && itemsLoader.loading
  const hasItemsError = isStatic && !!itemsLoader.error
  const totalForDisplay = isStatic
    ? itemsLoader.totalExpected ?? shelfMeta.items_total ?? 0
    : mergedItems.length
  const loadedCount = isStatic ? itemsLoader.loadedCount : mergedItems.length

  const isDynamic = stockTaking.mode === 'dynamic'
  const isChecked = shelfMeta.status === 'checked'

  return (
    <div>
      <PageHeader
        items={[
          'stocks',
          'stocks.stock_takings',
          { label: stockTaking.name, path: `/stock_takings/${stockTakingId}` },
          { label: shelfMeta.shelf_name ?? '' },
        ]}
      />

      <Card
        title={
          <Space>
            <span>{t('shared.stock_takings.shelf.title')}</span>
            <Tag>{shelfMeta.shelf_name}</Tag>
            <Tag color={isChecked ? 'green' : 'default'}>
              {t(`enums.stock_taking_shelf.status.${shelfMeta.status}`)}
            </Tag>
          </Space>
        }
        extra={
          isEditable && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={submitting}
              disabled={isLoadingItems || hasItemsError}
            >
              {t('shared.stock_takings.shelf.submit_button')}
            </Button>
          )
        }
      >
        <Descriptions bordered column={4} size="small">
          <Descriptions.Item label={t('models.stock_taking.name')}>
            {stockTaking.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.stock_taking.mode')}>
            <Tag color={shelfMeta.mode === 'static' ? 'purple' : 'cyan'}>
              {t(`enums.stock_taking.mode.${shelfMeta.mode}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('models.stock_taking.is_blind')}>
            <Tag color={stockTaking.is_blind ? 'orange' : 'default'}>
              {t(`shared.stock_takings.list.blind_types.${stockTaking.is_blind ? 'blind' : 'normal'}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.stock_takings.shelf.filled_count')}>
            <Tag color={filledCount > 0 ? 'blue' : 'default'}>
              {filledCount} / {totalForDisplay}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        {isDynamic && !isCompleted && (
          <Alert
            message={t('shared.stock_takings.show.dynamic_mode_hint')}
            description={t('shared.stock_takings.show.dynamic_mode_hint_desc')}
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        {isEditable && (
          <Alert
            message={t('shared.stock_takings.shelf.instruction_title')}
            description={
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>{t('shared.stock_takings.shelf.instruction_1')}</li>
                <li>{t('shared.stock_takings.shelf.instruction_2')}</li>
                <li>{t('shared.stock_takings.shelf.instruction_3')}</li>
              </ul>
            }
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      <Card
        title={t('shared.stock_takings.shelf.items_title')}
        style={{ marginTop: 16 }}
      >
        {hasItemsError && (
          <Alert
            type="error"
            showIcon
            message={t('shared.stock_takings.shelf.load_failed')}
            description={itemsLoader.error}
            action={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={itemsLoader.retry}
              >
                {t('shared.stock_takings.shelf.load_retry')}
              </Button>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {isLoadingItems ? (
          <LoadingItemsOverlay
            loaded={loadedCount}
            total={itemsLoader.totalExpected}
            label={t('shared.stock_takings.shelf.loading_items_initial')}
            progressLabel={t}
          />
        ) : isDynamic && !isCompleted && mergedItems.length === 0 ? (
          <Empty description={t('shared.stock_takings.shelf.dynamic_no_items')} />
        ) : (
          <Table<MergedRow>
            rowKey="inventory_id"
            columns={columns}
            dataSource={mergedItems}
            pagination={false}
            virtual
            scroll={{ x: 900, y: VIRTUAL_SCROLL_HEIGHT }}
            size="small"
          />
        )}
      </Card>
    </div>
  )
}

// 載入進度區塊：BE 還沒回首頁 total 時顯示「正在準備項目...」，total 出現後顯示
// 進度條 + 「載入中... X / Y」。期間外層 submit / 編輯按鈕都 disable。
function LoadingItemsOverlay({
  loaded,
  total,
  label,
  progressLabel,
}: {
  loaded: number
  total: number | null
  label: string
  progressLabel: (key: string, opts?: Record<string, unknown>) => string
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: 16 }}>
      <Spin size="large" />
      {total === null ? (
        <Typography.Text>{label}</Typography.Text>
      ) : (
        <>
          <Typography.Text>
            {progressLabel('shared.stock_takings.shelf.loading_items_progress', { loaded, total })}
          </Typography.Text>
          <Progress
            percent={total > 0 ? Math.floor((loaded / total) * 100) : 0}
            status="active"
            style={{ width: 360 }}
          />
        </>
      )}
    </div>
  )
}
